"use client";

import { AddEventDialog } from "@/components/app/tasks/add-event-dialog";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useJourneyData } from "@/hooks/use-journey-data";
import { cn } from "@/lib/utils";
import type { CalendarEventFormValues } from "@/schemas/calendar";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Plus,
} from "lucide-react";
import { useState } from "react";

interface InspectionType {
  id: string;
  name: string;
  description: string;
  duration: number; // in minutes
  recommended: boolean;
  suggestedTimeOfDay?: "morning" | "afternoon";
}

const commonInspections: InspectionType[] = [
  {
    id: "general",
    name: "General Home Inspection",
    description:
      "Comprehensive evaluation of the home's major systems and structure",
    duration: 180,
    recommended: true,
    suggestedTimeOfDay: "morning",
  },
  {
    id: "radon",
    name: "Radon Testing",
    description: "48-hour test to measure radon gas levels",
    duration: 60,
    recommended: true,
  },
  {
    id: "termite",
    name: "Termite/Pest Inspection",
    description: "Check for wood-destroying insects and pests",
    duration: 60,
    recommended: true,
  },
  {
    id: "sewer",
    name: "Sewer Scope",
    description: "Camera inspection of main sewer line",
    duration: 60,
    recommended: true,
  },
  {
    id: "foundation",
    name: "Foundation Inspection",
    description: "Detailed evaluation of foundation and structural elements",
    duration: 120,
    recommended: false,
  },
  {
    id: "roof",
    name: "Roof Inspection",
    description: "Detailed evaluation of roof condition and remaining life",
    duration: 90,
    recommended: false,
    suggestedTimeOfDay: "morning",
  },
  {
    id: "hvac",
    name: "HVAC Inspection",
    description: "Detailed evaluation of heating and cooling systems",
    duration: 90,
    recommended: false,
  },
  {
    id: "electrical",
    name: "Electrical Inspection",
    description: "Detailed evaluation of electrical systems",
    duration: 90,
    recommended: false,
  },
  {
    id: "plumbing",
    name: "Plumbing Inspection",
    description: "Detailed evaluation of plumbing systems",
    duration: 90,
    recommended: false,
  },
];

export function InspectionScheduler() {
  const [open, setOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] =
    useState<InspectionType | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const { createEvent, deleteCalendarEvent, calendarEvents, journeyData } =
    useJourneyData();

  // Check for active journey
  if (!journeyData?.id) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto space-y-6">
          <div className="bg-primary/5 rounded-2xl p-8 mx-auto">
            <div className="bg-primary/10 rounded-full p-5 w-20 h-20 mx-auto flex items-center justify-center">
              <Calendar className="w-10 h-10 text-primary" />
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">
              No Active Journey
            </h3>
            <p className="text-gray-600 max-w-sm mx-auto">
              You need to create or select a journey before you can schedule
              inspections.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Filter calendar events to only show inspection events
  const inspectionEvents = calendarEvents.filter(
    (event) => event.type === "inspection",
  );

  const handleSelect = (inspection: InspectionType) => {
    setSelectedInspection(inspection);
    setOpen(false);
    setIsCalendarOpen(true);
  };

  const handleScheduleEvent = (values: CalendarEventFormValues) => {
    if (!selectedInspection) return;

    createEvent.mutate({
      title: `${selectedInspection.name} - Due Diligence`,
      type: "inspection",
      description: selectedInspection.description,
      start_at: values.start_at.toISOString(),
      end_at: values.end_at.toISOString(),
      location: values.location,
      is_all_day: values.is_all_day,
    });

    setIsCalendarOpen(false);
    setSelectedInspection(null);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Schedule Inspections</h2>
            <p className="text-sm text-muted-foreground">
              Schedule recommended inspections during your due diligence period
            </p>
          </div>
          <div className="ml-auto">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" disabled={createEvent.isPending}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Inspection
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="end">
                <Command>
                  <CommandInput placeholder="Search inspections..." />
                  <CommandEmpty>No inspections found.</CommandEmpty>
                  <CommandGroup>
                    <ScrollArea className="h-[300px]">
                      {commonInspections.map((inspection) => (
                        <CommandItem
                          key={inspection.id}
                          value={inspection.name}
                          onSelect={() => handleSelect(inspection)}
                          className="flex items-start p-4 hover:bg-accent"
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              <span className="font-semibold">
                                {inspection.name}
                              </span>
                              {inspection.recommended && (
                                <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {inspection.description}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Duration: {inspection.duration / 60} hours
                            </p>
                          </div>
                        </CommandItem>
                      ))}
                    </ScrollArea>
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="mt-4">
          <AnimatePresence mode="wait">
            {inspectionEvents.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-2"
              >
                {inspectionEvents.map((event) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className={cn(
                      "flex items-center justify-between rounded-md border p-3",
                      "bg-background transition-colors hover:bg-accent",
                    )}
                  >
                    <div className="flex items-center">
                      <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
                      <div className="flex flex-col">
                        <span className="font-semibold">{event.title}</span>
                        <span className="text-sm text-muted-foreground">
                          {new Date(event.start_at).toLocaleDateString()} at{" "}
                          {new Date(event.start_at).toLocaleTimeString([], {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (event.id) {
                          deleteCalendarEvent.mutate(event.id);
                        }
                      }}
                      disabled={deleteCalendarEvent.isPending}
                    >
                      Remove
                    </Button>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-8 text-center"
              >
                <div className="rounded-full bg-muted p-4">
                  <ClipboardList className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="mt-4 font-semibold">No Inspections Scheduled</h3>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                  Start by scheduling your home inspection. We recommend
                  completing all inspections within the first week of your due
                  diligence period.
                </p>
                <Button onClick={() => setOpen(true)} className="mt-4">
                  <Calendar className="mr-2 h-4 w-4" />
                  Schedule Your First Inspection
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {selectedInspection && (
        <AddEventDialog
          open={isCalendarOpen}
          onOpenChange={setIsCalendarOpen}
          onSubmit={handleScheduleEvent}
          selectedDate={new Date()}
          defaultValues={{
            title: `${selectedInspection.name} - Due Diligence`,
            type: "inspection",
            description: selectedInspection.description,
            duration: selectedInspection.duration,
          }}
        />
      )}
    </>
  );
}
